import { useEffect, useMemo, useState } from "react";
import { Users2, IndianRupee, CheckCircle2, Clock, Search, ScrollText } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const INR0 = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function DirectIncome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/direct-income");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    const qq = q.trim().toLowerCase();
    if (!qq) return data.items;
    return data.items.filter((r) => {
      const dateStr = r.date ? new Date(r.date).toLocaleString("en-IN") : "";
      return (
        r.referral_id.toLowerCase().includes(qq) ||
        (r.name || "").toLowerCase().includes(qq) ||
        dateStr.toLowerCase().includes(qq)
      );
    });
  }, [data, q]);

  const formatDT = (iso) => {
    if (!iso) return { d: "—", t: "" };
    try {
      const dt = new Date(iso);
      return {
        d: dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
        t: dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
      };
    } catch { return { d: "—", t: "" }; }
  };

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">

        {/* Title */}
        <section className="bg-white rounded-2xl shadow-lg p-6">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            Direct Income
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Earn Rs. 200 for each referral when their ID is activated
          </p>
        </section>

        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <>
            {/* Teal summary card */}
            <section
              data-testid="direct-income-summary"
              className="rounded-2xl p-6 text-white shadow-xl grid grid-cols-1 sm:grid-cols-2 gap-6"
              style={{
                background: "linear-gradient(90deg, #14b8a6 0%, #0ea5e9 100%)",
              }}
            >
              <div>
                <div className="text-sm text-white/85">Your Direct Income</div>
                <div className="mt-1 text-4xl font-black tracking-tight"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {INR0(data.total_income)}
                </div>
                <div className="text-xs text-white/80 mt-1">
                  Rs. {data.per_referral} per activated referral
                </div>
              </div>
              <div>
                <div className="text-sm text-white/85">Total Activated Referrals</div>
                <div data-testid="active-directs-count"
                     className="mt-1 text-4xl font-black tracking-tight"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.active_directs}
                </div>
                <div className="text-xs text-white/80 mt-1">
                  Referrals with activated IDs
                </div>
              </div>
            </section>

            {/* Breakdown */}
            <section className="bg-white rounded-2xl shadow-lg p-5">
              <h2 className="font-extrabold text-slate-900 text-lg mb-4"
                  style={{ fontFamily: "Outfit, sans-serif" }}>
                Income Breakdown
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-teal-50 border-l-4 border-teal-500 p-4">
                  <div className="text-sm text-slate-600">Total Income</div>
                  <div className="mt-1 text-2xl font-extrabold text-teal-700 tabular-nums"
                       style={{ fontFamily: "Outfit, sans-serif" }}>
                    {INR0(data.total_income)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Total earnings (credited + pending)
                  </div>
                </div>
                <div className="rounded-xl bg-emerald-50 border-l-4 border-emerald-500 p-4">
                  <div className="text-sm text-slate-600">Credited Amount</div>
                  <div className="mt-1 text-2xl font-extrabold text-emerald-700 tabular-nums"
                       style={{ fontFamily: "Outfit, sans-serif" }}>
                    {INR0(data.credited)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Ready to withdraw</div>
                </div>
                <div className="rounded-xl bg-amber-50 border-l-4 border-amber-500 p-4">
                  <div className="text-sm text-slate-600">Pending Amount</div>
                  <div className="mt-1 text-2xl font-extrabold text-amber-700 tabular-nums"
                       style={{ fontFamily: "Outfit, sans-serif" }}>
                    {INR0(data.pending)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Awaiting credit</div>
                </div>
              </div>
            </section>

            {/* History */}
            <section data-testid="direct-income-history"
                     className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h2 className="text-xl font-extrabold flex items-center gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <ScrollText size={18} className="text-red-700" />
                  Direct Income History
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Only <b>ACTIVE</b> referrals (with completed topup activation) earn direct income
                </p>
              </div>

              <div className="px-5 pb-4">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="direct-income-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by Referral ID, Name, or Date..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
                    <tr>
                      <th className="text-left px-4 py-3">S.No</th>
                      <th className="text-left px-4 py-3">Referral ID</th>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Package</th>
                      <th className="text-left px-4 py-3">Amount</th>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody data-testid="direct-income-table" className="divide-y divide-slate-100">
                    {filtered.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-500">
                        No active referrals yet. Share your referral link to start earning.
                      </td></tr>
                    ) : filtered.map((r, i) => {
                      const a = formatDT(r.date);
                      return (
                        <tr key={r.referral_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-800">{i + 1}</td>
                          <td className="px-4 py-3 font-mono text-blue-700 font-semibold">{r.referral_id}</td>
                          <td className="px-4 py-3 font-semibold">{r.name}</td>
                          <td className="px-4 py-3 text-slate-700">{r.package}</td>
                          <td className="px-4 py-3 font-extrabold text-emerald-600 tabular-nums">
                            {INR(r.amount)}
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            <div className="font-semibold">{a.d},</div>
                            <div className="text-xs text-slate-500">{a.t}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 text-xs font-semibold px-2.5 py-1 rounded-full">
                              <CheckCircle2 size={12} /> Credited
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600 text-center">
                Showing <b>1-{filtered.length}</b> of <b>{data.items.length}</b> records
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
